import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  MDBContainer, MDBCard, MDBCardBody, MDBCardHeader, MDBBtn, MDBBox
} from 'mdbreact';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Loading from 'components/ui-components/loading';
import addNotification, { toMessageString } from 'helpers/notify';
import { getTargetSourceUrl } from 'helpers/socialConnectCTX';
import { applyConnectPayload , setDefaultSocialTarget } from 'actions/social';         // <-- adjust path
import {
  selectSocialAccountById,
  selectTargetsWithStatusByAccount,
  selectDefaultTargetForAccount
} from 'selectors/social';                                      // <-- adjust path

import {
  getSocialConnectData,
  getSocialAccount,
  getSocialAccountTargets,
  saveSocialTarget,
  getSocialError,
  connectSocialMedia
} from '../../api/socialMedia/socialMedia';

 import {
   selectBrandById,
   selectBrandSettingsById,
   selectPlatformConnectionForBrandSettings
 } from 'selectors/social';


const BRAND_NAVY = 'var(--brand-navy, #0B3D91)';
const BRAND_NAVY_DARK = 'var(--brand-navy-dark, #092d70)';

const PLATFORM_COPY = {
  linkedin: { name: 'LinkedIn', profileNote: 'To connect a LinkedIn Company Page, you must have at least Content Admin access.', adsNote: 'To connect a LinkedIn Ad Account, the account must be active and you must have Campaign Manager access.' },
  facebook: { name: 'Facebook', profileNote: 'To connect a Facebook Page, you must have Admin access.', adsNote: 'To connect a Facebook Ads Account, you must have Ads Manager access.' },
  instagram:{ name: 'Instagram', profileNote: 'Instagram posts through connected Facebook Pages with Instagram Business accounts.', adsNote: 'Instagram ads are managed through Facebook Ads Manager.' },
  twitter:  { name: 'Twitter/X', profileNote: 'To connect a Twitter/X account, you must be an account administrator.', adsNote: 'To connect Twitter Ads, you must have Ads Manager permissions.' },
  pinterest:{ name: 'Pinterest', profileNote: 'Connect your Pinterest account to publish pins to your boards.', adsNote: 'Pinterest Ads require Business account access.' },
  youtube:  { name: 'YouTube', profileNote: 'Connect your YouTube channel to manage video content.', adsNote: 'YouTube advertising is managed through Google Ads.' },
  tiktok:   { name: 'TikTok', profileNote: 'Connect your TikTok account to publish videos.', adsNote: 'TikTok Ads Manager access required for advertising.' },
  // Without these, any non-listed platform fell back to PLATFORM_COPY.facebook
  // below (`PLATFORM_COPY[platform] || PLATFORM_COPY.facebook`), so connecting
  // Snapchat/etc. mislabeled the whole panel as "Facebook".
  snapchat: { name: 'Snapchat', profileNote: 'Connect a Snapchat Public Profile to post Stories & Spotlight (requires Public Profile API access).', adsNote: 'Snapchat Ads are managed through Snapchat Ads Manager.' },
  google:   { name: 'Google', profileNote: 'Connect your Google Business Profile to manage posts.', adsNote: 'Google Ads access required for advertising.' },
  reddit:   { name: 'Reddit', profileNote: 'Connect your Reddit account to submit posts to subreddits.', adsNote: 'Reddit Ads are managed through the Reddit Ads dashboard.' },
  telegram: { name: 'Telegram', profileNote: 'Connect a Telegram bot token to post to a channel or group.', adsNote: '' },
  discord:  { name: 'Discord', profileNote: 'Connect a Discord webhook URL to post to a channel.', adsNote: '' }
};

// Option “badges” (emoji + color; works inside <option>)
const STATUS_META = {
  connected:     { dot: '🟢', color: '#2e7d32', label: 'Connected' },
  no_target:     { dot: '🟡', color: '#f9a825', label: 'No target selected', tooltip: 'Account is authenticated but no publishing destination is selected. Pick one to enable posting.' },
  expired:       { dot: '🟠', color: '#ed6c02', label: 'Expired' },
  failed:        { dot: '🔴', color: '#c62828', label: 'Failed' },
  pending:       { dot: '🟡', color: '#f9a825', label: 'Pending' },
  disconnected:  { dot: '⚪', color: '#9e9e9e', label: 'Disconnected' },
  not_connected: { dot: '⚪', color: '#bdbdbd', label: 'Not connected' }
};

function useBrandContextFromUrlOrProps(props) {
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const brandId =
    props.brandId ||
    qs.get('brandId') ||
    undefined;

  const brandSettingsId =
    props.brandSettingsId ||
    qs.get('brandSettingsId') ||
    undefined;

  return { brandId, brandSettingsId };
}

function renderTargetOptionLabel(t) {
  const meta = STATUS_META[t.targetStatus] || STATUS_META.disconnected;
  const typeSuffix = t.type ? ` (${String(t.type).replace(/_/g, ' ')})` : '';
  return `${meta.dot} ${t.name}${typeSuffix}`;
}

function safeNotify(msg, type) {
  try { addNotification && addNotification(msg, type || 'info'); }
  catch (_) { (type === 'danger' ? console.error : console.log)(msg); }
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function normalizeTargets(platform, targets, user) {
  const out = [];
  if (!Array.isArray(targets)) return out;

  targets.forEach((t) => {
    if (!t) return;
    const id = t.id || t.pageId || t.channelId || t.username || t.urn;
    if (id == null) return;

    const name =
      t.name || t.pageName || t.title || t.localizedName || t.username || 'Destination';

    let type = t.type;
    if (!type) {
      if (platform === 'linkedin') type = String(id).includes('organization') ? 'li_organization' : 'li_person';
      else if (platform === 'facebook') type = 'facebook_page';
      else if (platform === 'instagram') type = 'instagram_business';
      else type = `${platform}_target`;
    }

    out.push({
      id: String(id),
      name,
      type,
      token: t.token || t.accessToken || t.pageAccessToken || null,
      avatarUrl: t.avatarUrl || t.pictureUrl || t.icon || null,
      tokenExpiresAt: t.tokenExpiresAt || null
    });
  });

  const map = new Map();
  out.forEach((x) => { if (!map.has(x.id)) map.set(x.id, x); });
  return Array.from(map.values());
}

function useDataLoader(platform, query, accountIdProp, targetsFromRedux, applyPayload) {

  const sourceRef = useRef('unknown'); // 'redux' | 'connectId' | 'session' | 'local' | 'accountFetch'  
  sourceRef.current = 'connectId'; // etc.


  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);

  const safeSet = useCallback((fn) => { if (mountedRef.current) fn(); }, []);

  const loadData = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      safeSet(() => { setLoading(true); setError(null); });

      const errorId = query.get('error');
      if (errorId) {
        try {
          const err = await getSocialError(errorId);
          throw new Error(err.error || 'Authentication failed');
        } catch (e) {
          throw new Error('Authentication failed');
        }
      }
// If Redux already has targets for this account, prefer them and stop early.
const hasReduxTargets = Array.isArray(targetsFromRedux) && targetsFromRedux.length > 0;
if (accountIdProp && hasReduxTargets) {
  safeSet(() => setLoading(false));
  return;
}
      const connectId = query.get('connectId');
      if (connectId) {
        try {
          const data = await getSocialConnectData(connectId);
          if (mountedRef.current && applyPayload(data)) {
            safeSet(() => setLoading(false));
            return;
          }
        } catch (e) {}
      }

      // window.name fallback. The backend's popup HTML stuffs the full
      // payload into window.name as "SOCIAL_PAYLOAD:<base64>" before
      // redirecting, and same-origin redirects preserve it. Used to
      // survive cases where the connectId cache is gone (TTL/restart).
      try {
        const wn = String(window.name || '');
        if (wn.startsWith('SOCIAL_PAYLOAD:')) {
          const blob = wn.slice('SOCIAL_PAYLOAD:'.length);
          const json = decodeURIComponent(escape(atob(blob)));
          const data = JSON.parse(json);
          window.name = ''; // single-use
          if (mountedRef.current && applyPayload(data)) {
            safeSet(() => setLoading(false));
            return;
          }
        }
      } catch (e) { /* ignore — fall through */ }

      const sessionKey = 'social:last';
      const ss = sessionStorage.getItem(sessionKey);
      if (ss) {
        try {
          const data = JSON.parse(ss);
          if (data.platform === platform && mountedRef.current && applyPayload(data)) {
            sessionStorage.removeItem(sessionKey);
            safeSet(() => setLoading(false));
            return;
          }
        } catch (e) { sessionStorage.removeItem(sessionKey); }
      }

      const localKey = `social:temp:${platform}`;
      const ls = localStorage.getItem(localKey);
      if (ls) {
        try {
          const data = JSON.parse(ls);
          if (mountedRef.current && applyPayload(data)) {
            localStorage.removeItem(localKey);
            safeSet(() => setLoading(false));
            return;
          }
        } catch (e) { localStorage.removeItem(localKey); }
      }

      const accountIdToLoad = query.get('accountId') || accountIdProp;
      if (accountIdToLoad) {
        try {
          let payload;
          try {
            const tdata = await getSocialAccountTargets(accountIdToLoad);
            payload = {
              platform: tdata.platform || platform,
              accountId: accountIdToLoad,
              user: tdata.user,
              targets: tdata.targets || [],
              adsAccounts: tdata.adsAccounts || []
            };
          } catch (e) {
            const acc = await getSocialAccount(accountIdToLoad);
            payload = {
              platform: acc.platform || platform,
              accountId: String(acc._id),
              user: acc.accountName ? { name: acc.accountName } : null,
              targets:
                acc.targets ||
                (acc.pages || []).map((p) => ({
                  id: p.pageId, name: p.pageName, type: p.type || `${acc.platform}_page`, token: p.accessToken
                })),
              adsAccounts: []
            };
          }

          if (mountedRef.current && applyPayload(payload)) {
            safeSet(() => setLoading(false));
            return;
          }
        } catch (e) {}
      }

      if (retryCount > 0) throw new Error('No destinations found. Please try reconnecting.');
      safeSet(() => setLoading(false));
    } catch (e) {
      const msg = (e && e.message) || 'Failed to load data';
      if (mountedRef.current) {
        safeSet(() => { setError(msg); setLoading(false); });
        safeNotify(msg, 'danger');
      }
    } finally {
      loadingRef.current = false;
    }
  }, [platform, query, accountIdProp, applyPayload, retryCount, safeSet]);

  const retry = useCallback(() => {
    setRetryCount(function (c) { return c + 1; });
    setError(null);
    loadData();
  }, [loadData]);

  useEffect(() => () => { mountedRef.current = false; }, []);
  return { loading, error, retry, loadData };
}

const SocialMediaConnect = (props) => {
  const { platform: platformFromUrl } = useParams();
  const query = useQuery();
  const dispatch = useDispatch();
  const mountedRef = useRef(true);

  const {
    platform: platformProp,
    targets: targetsProp = [],
    accountId: accountIdProp,
       onClose,
  //  brandId: brandIdProp,
  //  brandSettingsId: brandSettingsIdProp,
  } = props;

  const { brandId: brandIdProp, brandSettingsId: brandSettingsIdProp } =
  useBrandContextFromUrlOrProps(props);

  const [platform, setPlatform] = useState((platformProp || platformFromUrl || 'unknown').toLowerCase());
  const [accountId, setAccountId] = useState(accountIdProp || '');
  const [user, setUser] = useState(null);
  const [rawTargets, setRawTargets] = useState(targetsProp);
  const [adsAccounts, setAdsAccounts] = useState([]);
  // Set when discovery resolved no identity/targets → expired/revoked token.
  const [needsReauth, setNeedsReauth] = useState(false);
  // Informational note for a valid-but-empty discovery (e.g. Google Business
  // Profile API access still pending).
  const [discoveryNote, setDiscoveryNote] = useState(null);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [selectedAdsId, setSelectedAdsId] = useState('');
  const [error, setError] = useState(null);



  // Redux-derived data (if already in store)
  const account = useSelector(accountId ? selectSocialAccountById(accountId) : function () { return undefined; });
  const targetsFromRedux = useSelector(accountId ? selectTargetsWithStatusByAccount(accountId) : function () { return []; });
  const defaultTarget = useSelector(accountId ? selectDefaultTargetForAccount(accountId) : function () { return undefined; });

  // Prefer the canonical entities in Redux
  const brandIdFromUrlOrProps = brandIdProp || query.get('brandId') || null;
  const brandSettingsIdFromUrlOrProps = brandSettingsIdProp || query.get('brandSettingsId') || null;

  // 1) brandId: props/query → account.owner → null
  const brandIdFromRedux = useSelector(state => {
    if (brandIdFromUrlOrProps) return String(brandIdFromUrlOrProps);
    if (accountId) {
      const acc = selectSocialAccountById(accountId)(state);
      if (acc?.brand_id) return String(acc.brand_id);
    }
  return null;
});

  // 2) brandSettings: props/query → brand.brandSettings → null
  const brandSettingsFromRedux = useSelector(state => {
    if (brandSettingsIdFromUrlOrProps) {
      return selectBrandSettingsById(brandSettingsIdFromUrlOrProps)(state) || null;
    }
    if (brandIdFromRedux) {
      const b = selectBrandById(brandIdFromRedux)(state);
      const bsId = b?.brandSettings?._id || b?.brandSettingsId;
      if (bsId) return selectBrandSettingsById(bsId)(state) || null;
    }
  return null;
});

const effectiveBrandId = brandIdFromRedux || null;
const effectiveBrandSettingsId = brandSettingsFromRedux?._id || brandSettingsIdFromUrlOrProps || null;

// "Use a different account": re-run the provider OAuth forcing its account/login
// picker (switchAccount=1 → per-platform: Google select_account, FB/IG re-auth,
// TikTok disable_auto_auth, X force_login). Navigating THIS popup to the fresh
// auth URL keeps the same /social/<platform>/callback, which re-discovers the
// new account's pages/profiles/channels.
const [switching, setSwitching] = useState(false);
const handleUseDifferentAccount = useCallback(async () => {
  try {
    setSwitching(true);
    const data = await connectSocialMedia(platform, {
      brandId: effectiveBrandId || undefined,
      brandSettingsId: effectiveBrandSettingsId || undefined,
      switchAccount: 1,
    });
    if (data && data.url) { window.location.href = data.url; return; }
    safeNotify('Could not start a different-account login.', 'danger');
    setSwitching(false);
  } catch (e) {
    safeNotify((e && e.message) || 'Could not start a different-account login.', 'danger');
    setSwitching(false);
  }
}, [platform, effectiveBrandId, effectiveBrandSettingsId]);

// Derive per-platform connection/status for this BrandSettings
const platformConn = useSelector(state =>
  selectPlatformConnectionForBrandSettings(effectiveBrandSettingsId, platform)(state)
);

// Final status used across UI & payload
const platformStatus = platformConn?.status || account?.status || 'disconnected';


  const applyPayload = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return false;
    if (!mountedRef.current) return false;

    const p = String(payload.platform || platform || '').toLowerCase();
    if (p && p !== 'unknown') setPlatform(p);
    if (payload.accountId) setAccountId(String(payload.accountId));
    if (payload.user) setUser(payload.user);
    if (Array.isArray(payload.targets)) setRawTargets(payload.targets);
    if (Array.isArray(payload.adsAccounts)) setAdsAccounts(payload.adsAccounts);
    setNeedsReauth(!!payload.needsReauth);
    setDiscoveryNote(payload.discoveryNote || null);

   // --- brand context resolution (payload/props → redux fallbacks) ---
   const effectiveBrandIdLocal =
     String(payload.brandId || payload.brand_id || brandIdProp || effectiveBrandId || '') || null;
   const effectiveBrandSettingsIdLocal =
     String(payload.brandSettingsId || brandSettingsIdProp || effectiveBrandSettingsId || '') || null;

    const normalizedTargets = normalizeTargets(p, payload.targets || [], payload.user);

    const thunkPayload = {
      brandId: effectiveBrandIdLocal,                  // ✅ critical
      brandSettingsId: effectiveBrandSettingsIdLocal,  // ✅ helpful for later
      account: {
        _id: String(payload.accountId || (payload.account && payload.account._id) || ''),
        platform: p,
        status: (payload.status || (payload.account && payload.account.status) || platformStatus || 'connected'),
        accountName: (payload.user && payload.user.name) || (payload.account && payload.account.accountName),
        avatar: payload.account && payload.account.avatar,
        socialHandle: payload.account && payload.account.socialHandle,
        socialHeading: payload.account && payload.account.socialHeading,
        followers: payload.account && payload.account.followers,
        expiresAt: (payload.account && payload.account.expiresAt) || null,
        defaultTarget: (payload.account && payload.account.defaultTarget) || null
      },
      targets: normalizedTargets.map(function (t) {
        return {
          platform: p,
          externalId: t.id,
          name: t.name,
          type: t.type,
          avatarUrl: t.avatarUrl,
          tokenExpiresAt: t.tokenExpiresAt || null,
          isDefault: false
        };
      }),
      defaultTargetExternalId:
        payload.defaultTargetExternalId ||
        (payload.account && payload.account.defaultTarget && payload.account.defaultTarget.id) ||
        (normalizedTargets[0] && normalizedTargets[0].id)
    };


    // If we still don't know the brand, bail early to avoid polluting state.
   if (!thunkPayload.brandId) {
     safeNotify('Could not determine brand for this connection.', 'danger');
     return false;
   }
try {
  sessionStorage.setItem('social:last', JSON.stringify({
    platform: p,
    accountId: thunkPayload.account._id,
    brandId: thunkPayload.brandId,
    brandSettingsId: thunkPayload.brandSettingsId,
    user: payload.user || null,
    targets: payload.targets || [],
    adsAccounts: payload.adsAccounts || [],
    defaultTargetExternalId: thunkPayload.defaultTargetExternalId || null
  }));
} catch {}
    dispatch(applyConnectPayload(thunkPayload));

    if (!selectedTargetId && thunkPayload.defaultTargetExternalId) {
      setSelectedTargetId(String(thunkPayload.defaultTargetExternalId));
    }
    return true;
}, [dispatch, platform, selectedTargetId, brandIdProp, brandSettingsIdProp, accountId, platformStatus]);

  const { loading, error: dataLoaderError, retry, loadData } = 
  useDataLoader (platform, query, accountIdProp, targetsFromRedux, applyPayload);

  const combinedError = error || dataLoaderError;

  useEffect(() => {
    function handleMessage(event) {
      if (!mountedRef.current || !event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'oauth:connected' && event.data.payload) {
        dispatch(applyConnectPayload(event.data.payload));
        applyPayload(event.data.payload);
      } else if (event.data.type === 'AUTH_SUCCESS' && event.data.data) {
        applyPayload(event.data.data);
      } else if (event.data.type === 'oauth:failed' || event.data.type === 'AUTH_FAILURE') {
        // event.data.error may be a RAW provider error object (FB Graph:
        // {message,type,code,fbtrace_id}); it must be a string before it
        // reaches the toast, the '. ' concat, or the combinedError render
        // ({combinedError.message}) — an object child throws React #31.
        const err = toMessageString(event.data.error) || 'Authentication failed';
        const suggestion = event.data.suggestion;
        safeNotify(suggestion ? (err + '. ' + suggestion) : err, 'danger');
        setError({ message: err, suggestion: suggestion, isUserError: event.data.isUserError, isConfigError: event.data.isConfigError });
      }
    }

    window.addEventListener('message', handleMessage);
    loadData();
    return () => window.removeEventListener('message', handleMessage);
  }, [dispatch, loadData, applyPayload]);

  useEffect(() => () => { mountedRef.current = false; }, []);

const targets = useMemo(() => {
  if (Array.isArray(targetsFromRedux) && targetsFromRedux.length) {
    return targetsFromRedux.map(t => ({
      id: t.externalId || t._id,
      name: t.name,
      type: t.type,
      avatarUrl: t.avatarUrl,
      tokenExpiresAt: t.tokenExpiresAt,
      targetStatus: t.targetStatus || platformStatus || 'disconnected'
    }));
  }
  return normalizeTargets(platform, rawTargets, user).map(t => ({
    ...t,
    targetStatus: platformStatus
  }));
}, [platform, rawTargets, user, targetsFromRedux, platformStatus]);

// Restore the user's previously-saved defaultTarget if one exists; otherwise
// leave the dropdown blank so the user has to make an explicit pick. We
// deliberately do NOT fall back to targets[0] — the dropdown's onChange
// auto-saves on pick, so silently preselecting the first option would
// look like the system "forced" a target without confirmation.
useEffect(() => {
  if (selectedTargetId) return;
  const preferred = account?.defaultTarget?.id || defaultTarget?.id || defaultTarget?.externalId;
  if (preferred) setSelectedTargetId(String(preferred));
}, [selectedTargetId, account, defaultTarget]);

// Prefer the ads account tied to the account's defaultTarget; otherwise first in list
useEffect(() => {
  if (!selectedAdsId && adsAccounts.length) {
    const byTarget = account?.defaultTarget?.adsAccountId;
    const match = byTarget && adsAccounts.find(a => String(a.id || a.accountId) === String(byTarget));
    const first = adsAccounts[0];
    setSelectedAdsId(String((match?.id || match?.accountId || first.id || first.accountId)));
  }
}, [adsAccounts, selectedAdsId, account]);

// In SocialMediaConnect.js - Updated saveSelection callback

const saveSelection = useCallback(async (overrideTargetId, opts) => {
  // Accepts an optional explicit target id so the dropdown's onChange can
  // save the freshly-picked value without waiting for the local state
  // update to flush (which otherwise would save the previous selection).
  // opts.closePopup (default true): controls whether the popup closes
  // when the save succeeds. Auto-saves from dropdown changes pass false
  // so the user can keep tweaking; the explicit "Save Connection" button
  // passes true (the default) to close.
  //
  // Defensive: when onClick={saveSelection} is wired without a wrapper,
  // React hands us the MouseEvent as the first arg — which stringifies
  // to "[object Object]" downstream and the backend correctly rejects
  // it. Discard non-string/non-number first args.
  const _validOverride =
    overrideTargetId != null &&
    (typeof overrideTargetId === 'string' || typeof overrideTargetId === 'number');
  const closePopup = !opts || opts.closePopup !== false;
  const targetIdToSave = _validOverride ? overrideTargetId : selectedTargetId;
  if (!targetIdToSave) return safeNotify('Please choose a destination', 'danger');
  if (!accountId) return safeNotify('Missing account ID', 'danger');

  // Try every place a target shape might live before giving up. Match
  // against id, externalId, _id, pageId, and the composed
  // `${platform}:${externalId}` key the user reducer sometimes stores.
  const eq = (val) => String(val ?? '') === String(targetIdToSave);
  const matchTarget = (t) => !!t && (
    eq(t.id) || eq(t.externalId) || eq(t._id) || eq(t.pageId) ||
    eq(`${platform}:${t.externalId || t.id || t.pageId || ''}`)
  );
  // rawTargets and account.pages are additional sources — rawTargets is
  // the original discovery payload (always populated after applyPayload);
  // account.pages mirrors it via the SocialAccount document.
  const rawTargetsList = Array.isArray(rawTargets) ? rawTargets : [];
  const accountPages = Array.isArray(account?.pages) ? account.pages : [];
  let chosen =
    (targets || []).find(matchTarget) ||
    (targetsFromRedux || []).find(matchTarget) ||
    rawTargetsList.find(matchTarget) ||
    accountPages.find(matchTarget) ||
    null;

  // Last-resort: fabricate, but pull the NAME from any list we can find a
  // partial match in (by pageId/externalId). The backend's SocialAccount
  // schema makes defaultTarget.name required, so an empty name 400's the
  // PUT with "Path `name` is required."
  if (!chosen) {
    const fallbackId = String(targetIdToSave).split(':').pop();
    const nameSources = [...rawTargetsList, ...accountPages, ...(targets || []), ...(targetsFromRedux || [])];
    const partial = nameSources.find(t => t && (
      String(t.id) === fallbackId || String(t.externalId) === fallbackId || String(t.pageId) === fallbackId
    ));
    chosen = {
      id: fallbackId,
      externalId: fallbackId,
      type: partial?.type || (platform === 'linkedin'
        ? (String(fallbackId).match(/^\d+$/) ? 'li_organization' : 'li_person')
        : `${platform}_target`),
      name: partial?.name || partial?.pageName || `Destination ${fallbackId}`,
      token: partial?.token || null
    };
    console.warn('[SMC] saveSelection: no target match in state; saving with fabricated chosen', { targetIdToSave, fallbackId, chosen });
  }

  try {
    // 1. Save to backend
const response = await saveSocialTarget(accountId, {
  platform,
  targetId: chosen.id || chosen.externalId,
  targetType: chosen.type,
  token: undefined,
  name: chosen.name,
  adsAccountId: selectedAdsId || null
});

    // 2. Update Redux store with the selection
    // This updates the brandSettings.selectedByPlatform for this platform
  // Prefer DB ids from the server, fall back to what we know
const selectedFromServer =
  response?.selected ||
  response?.brandSettings?.selectedByPlatform?.[platform] ||
  null;

/**
 * If your reducer expects DB IDs (ObjectIds of SocialAccount/SocialTarget/SocialAdsAccount),
 * use selectedFromServer.accountId / targetId / adsId.
 * If your reducer still uses *external* ids, keep your old fields and (optionally) pass DB ids under `dbIds`.
 */
dispatch({
  type: 'UPDATE_BRAND_SETTINGS_SELECTION',
  payload: {
    brandId: effectiveBrandId,
    brandSettingsId: effectiveBrandSettingsId,
    platform,

    // ---- Choose ONE of the following styles your reducer expects ----
    // A) Reducer expects DB ids (recommended):
    accountId: selectedFromServer?.accountId || accountId,
    targetId:  selectedFromServer?.targetId  || (chosen._id || `${platform}:${chosen.id || chosen.externalId}`),
    adsAccountId: selectedFromServer?.adsId  || null,

    // B) If you still key by *external* ids, keep the old fields and ALSO include DB ids for later:
    external: {
      targetExternalId: chosen.id || chosen.externalId,
      adsExternalId: selectedAdsId || null
    },
    dbIds: selectedFromServer ? {
      accountId: selectedFromServer.accountId || null,
      targetId:  selectedFromServer.targetId  || null,
      adsId:     selectedFromServer.adsId     || null
    } : null
  }
});

    // 3. Also update the social DRAFT reducer so the wizard's
    //    SocialMediaAccounts row (which reads from
    //    social.drafts[brandSettingsId][platform]) reflects the
    //    server-confirmed pick. Without `brandSettingsId` on this
    //    action the reducer writes to the global connections slice
    //    instead of the draft, and SMA's overlay stays stale until
    //    the brand commits.
    dispatch({
      type: 'SOCIAL_TARGET_SELECTED',
      platform: platform,
      brandSettingsId: effectiveBrandSettingsId || 'new',
      payload: {
        target: {
          id: chosen.id || chosen.externalId,
          name: chosen.name,
          type: chosen.type,
          avatarUrl: chosen.avatarUrl,
          token: chosen.token
        },
        adsAccountId: selectedAdsId || null
      }
    });

    // 4. If this should be the default target, set it
    dispatch(
      setDefaultSocialTarget(
        accountId,
        platform,
        `${platform}:${chosen.id || chosen.externalId}`
      )
    );

    // (The success notification fires once at the end of the save path —
    // see the safeNotify call after the postMessage block below. Don't
    // also fire it here; it produced a duplicate toast.)
    dispatch({
      type: 'SOCIAL_DRAFT_CLEAR',
      brandSettingsId: effectiveBrandSettingsId || 'new'
    });

// wipe ephemeral caches for this connect flow
try {
  sessionStorage.removeItem('social:last');
  localStorage.removeItem(`social:temp:${platform}`);
} catch {}


// clear overlay draft + ephemeral caches for this flow
dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: effectiveBrandSettingsId || 'new' });
try {
  sessionStorage.removeItem('social:last');
  localStorage.removeItem(`social:temp:${platform}`);
} catch {}


// let parent lift the brand/selection into its state BEFORE closing UI
if (props.onSave) {
  props.onSave({
    accountId,
    platform,
    targetId: chosen.id || chosen.externalId,
    brandId: effectiveBrandId
  });
}

// Notify the opener (SocialMediaAccounts in the create-brand modal) so
// it refreshes its brand state. SMA listens for 'CONNECTION_SAVED' and
// calls refresh(). Send this on EVERY save — including auto-saves from
// dropdown changes — so the opener stays in sync even when the popup
// stays open for further tweaks.
try {
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage({
      type: 'CONNECTION_SAVED',
      data: {
        accountId,
        platform,
        targetId: chosen.id || chosen.externalId,
        targetName: chosen.name,
        // Carry the ads selection through so SMA's row keeps the
        // ads subtitle in sync with the server-confirmed save.
        adsAccountId: selectedAdsId || null,
        adsAccountName: (function () {
          if (!selectedAdsId) return null;
          const a = (adsAccounts || []).find(function (x) {
            return String(x.id || x.accountId) === String(selectedAdsId);
          });
          return a ? (a.name || a.accountName || String(a.id || a.accountId)) : null;
        })(),
        brandId: effectiveBrandId,
        brandSettingsId: effectiveBrandSettingsId
      }
    }, '*');
  }
} catch (_) {}

safeNotify('Connection saved successfully!', 'success');

if (closePopup) {
  if (typeof props.onClose === 'function') props.onClose();
  else if (window.opener && !window.opener.closed) window.close();
}
    
  } catch (e) {
    const msg = (e && e.message) || 'Failed to save destination';
    safeNotify(msg, 'danger');
  }
}, [
  accountId, 
  platform, 
  selectedAdsId, 
  selectedTargetId, 
  targets, 
  targetsFromRedux, 
  dispatch, 
  props,
  effectiveBrandId,
  effectiveBrandSettingsId
]);
  const copy = PLATFORM_COPY[platform] || PLATFORM_COPY.facebook;

  return (
    <MDBContainer className="py-5">
      <MDBCard className="shadow-1" style={{ border: '1px solid rgba(11,61,145,0.13)' }}>
        <MDBCardHeader className="text-center" style={{ backgroundColor: BRAND_NAVY, color: '#fff', borderBottom: `2px solid ${BRAND_NAVY_DARK}` }}>
          <h4 className="mb-0">
            {loading ? ('Loading ' + copy.name + '...') : ('Connect to ' + copy.name)}
          </h4>
        </MDBCardHeader>

        <MDBCardBody className="px-4 py-4">
          {loading ? (
            <MDBBox className="text-center py-5">
              <Loading color="black" text={'Discovering your ' + copy.name + ' destinations...'} />
            </MDBBox>
          ) : combinedError ? (
            <MDBBox className="text-center py-4">
              <div className="text-danger mb-3">
                {toMessageString(typeof combinedError === 'object' ? combinedError.message : combinedError)}
              </div>
              <MDBBtn color="primary" onClick={retry}
                style={{ backgroundColor: BRAND_NAVY, borderColor: BRAND_NAVY }}>
                Try Again
              </MDBBtn>
            </MDBBox>
          ) : (
            <>
              <div className="px-3 py-2 mb-4 rounded" style={{ backgroundColor: 'rgba(11,61,145,0.05)', border: '1px solid rgba(11,61,145,0.15)' }}>
                <small>Choose where to publish your {copy.name} content.</small>
              </div>

              {user ? (
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle mr-2" style={{ width: 8, height: 8, background: (STATUS_META[platformStatus]||STATUS_META.disconnected).color }} />
                    <small className="text-muted">
                      { (STATUS_META[platformStatus]||STATUS_META.disconnected).dot } Status:&nbsp;
                      <strong>{(STATUS_META[platformStatus]||STATUS_META.disconnected).label}</strong>
                      {user?.name ? <> &nbsp;• Connected as <strong>{user.name}</strong></> : null}
                    </small>
                  </div>
                </div>
              ) : null}

              <div className="mb-4">
                <label className="mb-2 font-weight-bold">
                  Publishing Destination
                  {/* Open currently selected target on its native platform */}
                  {selectedTargetId ? (() => {
                    const sel = targets.find(t => String(t.id) === String(selectedTargetId));
                    const handle = (user && user.username) || (sel && sel.username) || null;
                    const url = sel ? getTargetSourceUrl(platform, sel.type, sel.id, sel.name, handle) : null;
                    return url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="ml-2" style={{ fontSize: '0.8rem', fontWeight: 400, color: '#0b3d91' }}
                        title={'View this destination on ' + copy.name}>
                        <i className="fas fa-external-link-alt" /> view
                      </a>
                    ) : null;
                  })() : null}
                </label>

                {/* Expired/revoked token: discovery returned no identity/targets.
                    Explain WHY the list is empty (instead of a blank picker) and
                    point the user at reconnecting. */}
                {needsReauth && !targets.length ? (
                  <div
                    className="alert mb-2"
                    role="alert"
                    style={{ background: '#fff8e1', border: '1px solid #f9a825', color: '#7a5b00', fontSize: '0.875rem' }}
                  >
                    <i className="fas fa-exclamation-triangle mr-2" />
                    Your {copy.name} session has expired or was revoked, so we couldn't
                    load any destinations. Please <strong>reconnect {copy.name}</strong> from
                    the Social Accounts page to refresh access — then your pages/profiles
                    will appear here.
                  </div>
                ) : null}

                {/* Valid connection but nothing to list (e.g. Google Business
                    Profile API access still pending). Explain instead of a blank
                    picker. Distinct from needsReauth (this token IS valid). */}
                {!needsReauth && discoveryNote && !targets.length ? (
                  <div
                    className="alert mb-2"
                    role="alert"
                    style={{ background: '#e7f1ff', border: '1px solid #0b3d91', color: '#0b3d91', fontSize: '0.875rem' }}
                  >
                    <i className="fas fa-info-circle mr-2" />
                    {discoveryNote}
                  </div>
                ) : null}

                <select
                  className="browser-default custom-select"
                  value={selectedTargetId}
                  onChange={function (e) {
                    const newId = e.target.value;
                    setSelectedTargetId(newId);
                    // Optimistic update: reflect the new pick across the
                    // app BEFORE the async save round-trip completes, so
                    // the parent SocialMediaAccounts page (and any other
                    // consumer reading from Redux) stops showing the old
                    // target name the moment the dropdown changes. The
                    // async saveSelection below will replace these with
                    // server-truth values; if it fails, the error toast
                    // tells the user to re-pick.
                    if (newId) {
                      // Find the chosen target across all the shapes it
                      // can have in state: the display-mapped `targets`
                      // (id/name/type) and the raw redux docs (which
                      // carry the Mongo `_id` SMA needs to render the
                      // name). Match by every id field we know.
                      const _matchById = function (t) {
                        if (!t) return false;
                        var nid = String(newId);
                        return (
                          String(t.id || '') === nid ||
                          String(t.externalId || '') === nid ||
                          String(t._id || '') === nid
                        );
                      };
                      const _chosen =
                        (targetsFromRedux || []).find(_matchById) ||
                        (targets || []).find(_matchById) ||
                        null;
                      const _platLower = String(platform || '').toLowerCase();
                      // The Mongo `_id` of the SocialTarget doc, when we
                      // can find it. SMA's selector matches `bpTargets[_id]`
                      // against selectedByPlatform[plat].targetId, so we
                      // MUST hand it the mongo id (not the platform-prefixed
                      // external id, which would never match).
                      const _targetMongoId =
                        _chosen && _chosen._id ? String(_chosen._id) : null;
                      const _targetExtId =
                        (_chosen && (_chosen.externalId || _chosen.id)) || newId;
                      if (effectiveBrandId) {
                        dispatch({
                          type: 'UPDATE_BRAND_SETTINGS_SELECTION',
                          payload: {
                            brandId: effectiveBrandId,
                            brandSettingsId: effectiveBrandSettingsId,
                            platform: _platLower,
                            accountId: accountId,
                            // Prefer mongo id; SMA can also bridge from
                            // account.defaultTarget.id when only ext is known.
                            targetId: _targetMongoId || _targetExtId,
                            external: { targetExternalId: _targetExtId, adsExternalId: selectedAdsId || null }
                          }
                        });
                        dispatch({
                          type: 'SET_BRAND_DEFAULT_TARGET',
                          payload: {
                            brandId: effectiveBrandId,
                            brandSettingsId: effectiveBrandSettingsId,
                            platform: _platLower,
                            accountId: accountId,
                            // Same: mongo id for the reducer's lookup,
                            // ext id as the secondary key the reducer also
                            // matches on (see SET_BRAND_DEFAULT_TARGET case).
                            targetId: _targetMongoId || _targetExtId,
                            externalTargetId: _targetExtId,
                            chosenName: _chosen ? (_chosen.name || null) : null
                          }
                        });
                      }
                      // CRUCIAL: SMA reads its row from the floating
                      // `social.drafts[brandSettingsId][platform]` slice
                      // during the wizard (before brand commit). The
                      // user.brands tree above mirrors server-truth
                      // (which doesn't exist yet for a draft brand).
                      // The dispatch below writes the dropdown pick into
                      // the draft overlay so SMA's
                      // `draftsByPlatform[plat].selectedTargetId` flips
                      // synchronously. Without this, SMA keeps rendering
                      // the previous selectedTargetId until the wizard
                      // commits the brand and the draft is consolidated
                      // into the user tree.
                      dispatch({
                        type: 'SOCIAL_TARGET_SELECTED',
                        platform: _platLower,
                        brandSettingsId: effectiveBrandSettingsId || 'new',
                        payload: {
                          target: {
                            id: _targetExtId,
                            name: _chosen ? _chosen.name : null,
                            type: _chosen ? _chosen.type : null,
                            avatarUrl: _chosen ? _chosen.avatarUrl : null,
                            token: _chosen ? _chosen.token : null
                          },
                          adsAccountId: selectedAdsId || null
                        }
                      });
                      // Tell the opener (SMA in the create-brand modal)
                      // to reflect the new target name immediately. SMA
                      // already listens for CONNECTION_SAVED.
                      // Look up the current ads-account name so SMA's
                      // row shows the right "Ads: X" subtitle alongside
                      // the new target. selectedAdsId is local state on
                      // this popup.
                      const _curAds = selectedAdsId
                        ? (adsAccounts || []).find(function (a) {
                            return String(a.id || a.accountId) === String(selectedAdsId);
                          })
                        : null;
                      const _curAdsName = _curAds
                        ? (_curAds.name || _curAds.accountName || String(_curAds.id || _curAds.accountId))
                        : null;
                      const _connSavedPayload = {
                        type: 'CONNECTION_SAVED',
                        data: {
                          accountId: accountId,
                          platform: _platLower,
                          targetId: newId,
                          targetName: _chosen ? _chosen.name : null,
                          adsAccountId: selectedAdsId || null,
                          adsAccountName: _curAdsName,
                          brandId: effectiveBrandId,
                          brandSettingsId: effectiveBrandSettingsId,
                          optimistic: true,
                          ts: Date.now()
                        }
                      };
                      // PRIMARY path: postMessage to the opener window
                      // (SMA in the create-brand modal listens for this).
                      try {
                        if (window.opener && !window.opener.closed) {
                          window.opener.postMessage(_connSavedPayload, '*');
                        }
                      } catch (_) { /* postMessage isn't critical */ }
                      // FALLBACK path: write to localStorage. SMA's
                      // window listens for 'storage' events so even if
                      // postMessage drops (popup security policy, origin
                      // mismatch, opener-closed race), the parent picks
                      // up the same payload on the next storage tick.
                      try {
                        window.localStorage.setItem(
                          'prigmar:connection_saved',
                          JSON.stringify(_connSavedPayload.data)
                        );
                      } catch (_) { /* localStorage may be unavailable */ }
                      saveSelection(newId, { closePopup: false });
                    }
                  }}
                  disabled={!targets.length}
                >
                  {!targets.length ? (
                    <option value="">No destinations available</option>
                  ) : (
                    <>
                      <option value="">Choose a destination…</option>
                      {targets.map(function (t) {
                        const meta = STATUS_META[t.targetStatus] || STATUS_META.disconnected;
                        return (
                          <option
                            key={t.id}
                            value={t.id}
                            style={{ color: meta.color }}
                            title={(t.name + ' • ' + meta.label)}
                          >
                            {renderTargetOptionLabel(t)}
                          </option>
                        );
                      })}
                    </>
                  )}
                </select>

                {/* mini legend */}
                <small className="text-muted d-block mt-2">
                  {Object.keys(STATUS_META).map(function (k) {
                    var m = STATUS_META[k];
                    return (
                      <span key={k} style={{ marginRight: 12 }}>
                        {m.dot} {m.label}
                      </span>
                    );
                  })}
                </small>

                <small className="text-muted d-block mt-2">{copy.profileNote}</small>
              </div>

              {adsAccounts.length > 0 ? (
                <div className="mb-4">
                  <label className="mb-2 font-weight-bold">Ads Account (Optional)</label>
                  <select
                    className="browser-default custom-select"
                    value={selectedAdsId}
                    onChange={function (e) {
                      const newAdsId = e.target.value;
                      setSelectedAdsId(newAdsId);
                      // Mirror the target-dropdown pattern: notify the
                      // opener SMA right away so the row's "Ads: X"
                      // subtitle re-renders before the async save. The
                      // SMA-local pickedByPlatform state is what SMA
                      // reads as the final override.
                      const _platLower = String(platform || '').toLowerCase();
                      const _curAds = newAdsId
                        ? (adsAccounts || []).find(function (a) {
                            return String(a.id || a.accountId) === String(newAdsId);
                          })
                        : null;
                      const _curAdsName = _curAds
                        ? (_curAds.name || _curAds.accountName || String(_curAds.id || _curAds.accountId))
                        : null;
                      // Look up current target info too so SMA's row
                      // keeps the target subtitle while only the Ads
                      // line changes.
                      const _curTarget = selectedTargetId
                        ? (targets || []).find(function (t) { return String(t.id) === String(selectedTargetId); })
                        : null;
                      try {
                        if (window.opener && !window.opener.closed) {
                          window.opener.postMessage({
                            type: 'CONNECTION_SAVED',
                            data: {
                              accountId: accountId,
                              platform: _platLower,
                              targetId: selectedTargetId || null,
                              targetName: _curTarget ? _curTarget.name : null,
                              adsAccountId: newAdsId || null,
                              adsAccountName: _curAdsName,
                              brandId: effectiveBrandId,
                              brandSettingsId: effectiveBrandSettingsId,
                              optimistic: true
                            }
                          }, '*');
                        }
                      } catch (_) { /* postMessage isn't critical */ }
                      // Persist when a target is already chosen — the
                      // ads selection is tied to the target via
                      // SocialAccount.defaultTarget.adsAccountId.
                      if (selectedTargetId) {
                        saveSelection(selectedTargetId, { closePopup: false });
                      }
                    }}
                  >
                    <option value="">Select an ads account...</option>
                    {adsAccounts.map(function (a) {
                      const val = a.id || a.accountId;
                      const label = a.name || a.accountName || val;
                      return (<option key={val} value={val}>{label}</option>);
                    })}
                  </select>
                  <small className="text-muted d-block mt-2">{copy.adsNote}</small>
                </div>
              ) : null}

              <div className="d-flex justify-content-end align-items-center mt-4">
                {/* Wrong account / want another profile? Re-run the provider login
                    forcing its account picker, then re-discover that account's
                    assets via the same callback. Works for every platform. */}
                <MDBBtn
                  color="link"
                  className="mr-auto p-0"
                  onClick={handleUseDifferentAccount}
                  disabled={switching}
                  style={{ textTransform: 'none', boxShadow: 'none', color: BRAND_NAVY, fontWeight: 600 }}
                  title={`Log in to ${copy.name} with a different account and load its pages/profiles.`}
                >
                  <i className="fas fa-user-friends mr-2" />
                  {switching ? 'Redirecting…' : 'Use a different account'}
                </MDBBtn>
                <MDBBtn color="white" className="mx-2" onClick={function () {
                  if (typeof props.onClose === 'function') props.onClose();
                  else if (window.opener && !window.opener.closed) window.close();
                  else window.history.back();
                }} style={{ border: '1px solid rgba(11,61,145,0.33)' }}>
                  Cancel
                </MDBBtn>
                <MDBBtn
                  color="primary"
                  onClick={function () { saveSelection(); }}
                  disabled={!selectedTargetId || !targets.length}
                  style={{ backgroundColor: BRAND_NAVY, borderColor: BRAND_NAVY }}
                >
                  Save Connection
                </MDBBtn>
              </div>
            </>
          )}
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default SocialMediaConnect;
